import { UUID } from "uuidv7"

import type { PropertyId } from "."

import { groupFor, PropertyGroup, PropertyStatus } from "."
import { InterfaceSelection } from "./InterfaceSelection"

export type EntityProperty = {
  id: string
  propertyId: string
  version: number
  schema: PropertySchema
  value: EntityPropertyValue
  timestamp: string
  deleted?: boolean
  status: PropertyStatus
  epochId?: number
}

export type Entity<EP extends EntityProperty> = {
  id: string
  properties: { [key in string]?: EP }
  selection: EntityPropertySelection
}

export abstract class BaseEntity<EP extends EntityProperty, E extends Entity<EP>> {
  abstract inner(): Readonly<E>

  abstract addProperty(property: EP): void
  abstract removeProperty(property: PropertyId): void
  /** Returns whether or not the entity was changed */
  abstract mergeWithNewer<T extends BaseEntity<EP, E>>(file?: T): boolean
  abstract addToBuf(builder: DatagramBufferBuilder): number

  static async idFromPrehash(preHash: string) {
    const [apiId, keyId] = partition(preHash, "-")
    const hash = await window.crypto.subtle.digest("SHA-256", UUID.parse(keyId).bytes)
    const uuid = UUID.ofInner(new Uint8Array(hash.slice(0, 16)))
    return `${apiId}-${uuid.toString()}` as PropertyId
  }

  get id(): string {
    return this.inner().id
  }

  get properties() {
    return this.inner().properties
  }

  get selection() {
    return new InterfaceSelection(this.inner().selection)
  }

  property(id: PropertyId): Readonly<EP | undefined> {
    return this.inner().properties?.[id]
  }

  isBlank() {
    return Object.keys(this.inner().properties).length === 0
  }

  /**
   * Staleness refers to whether our locally cached copy of the file is out of date, such as
   * when we are retrieving a new version from the server. Technically, only properties can
   * be stale, but for this function we just return true if any are stale
   *
   * @returns whether any of the properties of this file are stale
   */
  isCurrentAsOfEpoch(cacheEpochId: number | undefined) {
    if (!isDefined(cacheEpochId))
      return false
    for (const property of Object.values(this.inner().properties)) {
      if ([PropertyGroup.FileClientOnlyProperties].includes(groupFor(property?.propertyId as PropertyId)))
        continue
      if (property?.epochId !== cacheEpochId)
        return false
    }
    return true
  }

  /**
   * Pending is a property which is at a higher version than the server, waiting to push its
   * updates to the server. This function returns true if any of the properties are pending.
   *
   * @returns whether any of the properties of this file are pending
   */
  isPending() {
    for (const property of Object.values(this.inner().properties))
      if (enumEq(property!.status, PropertyStatus.StatusPending))
        return true
    return false
  }
}
